/**
 * RescheduleDialog — «Перенести срок». Меняет плановую дату транша (PATCH plannedDate).
 * Сервер закроет старую задачу и создаст новую (это его ответственность — мы лишь шлём дату).
 */

import { useState } from 'react';
import type { PaymentDto } from '../../../lib/types';
import { fmtDate } from '../lib/dates';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { DatePicker } from '../ui/DatePicker';
import { useUpdatePayment, type DealScope } from '../api';
import { ApiError } from '../../../lib/apiClient';

export interface RescheduleDialogProps {
  scope: DealScope;
  payment: PaymentDto;
  onClose: () => void;
}

export function RescheduleDialog({ scope, payment, onClose }: RescheduleDialogProps): JSX.Element {
  const [date, setDate] = useState<string>(payment.plannedDate);
  const [dateOpen, setDateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdatePayment(scope);

  const unchanged = date === payment.plannedDate;

  const submit = (): void => {
    setError(null);
    update.mutate(
      { id: payment.id, version: payment.version, patch: { plannedDate: date } },
      {
        onSuccess: () => onClose(),
        onError: (e) => {
          if (e instanceof ApiError && e.isVersionConflict) {
            setError('Транш изменён в другом месте — данные обновлены, повторите перенос.');
          } else {
            setError(e instanceof ApiError ? e.message : 'Не удалось перенести срок.');
          }
        },
      },
    );
  };

  return (
    <Dialog
      title={`Перенос срока транша №${payment.number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Отмена
          </Button>
          <Button variant="primary" onClick={submit} disabled={unchanged || update.isPending}>
            {update.isPending ? 'Перенос…' : 'Перенести'}
          </Button>
        </>
      }
    >
      <div className="pw-field">
        <span className="pw-field__label">Текущая плановая дата</span>
        <span className="pw-num pw-muted">{fmtDate(payment.plannedDate)}</span>
      </div>

      <div className="pw-field">
        <span className="pw-field__label">Новая плановая дата</span>
        <DatePicker
          value={date}
          open={dateOpen}
          onOpenChange={setDateOpen}
          onSelect={(ymd) => {
            if (ymd) setDate(ymd);
          }}
          trigger={
            <button type="button" className="pw-input pw-input--date" aria-label="Новая плановая дата">
              {fmtDate(date)}
            </button>
          }
        />
      </div>

      <p className="pw-hint">Старая задача-напоминание будет закрыта, новая создастся на выбранную дату.</p>

      {error ? (
        <p className="pw-field__err" role="alert">
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}
